import { axiosInstance } from '@/utils/lib/axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { env } from '@/utils/constants/env';
import {
  ChecklistDiscussion,
  CreateDiscussionPayload,
  UpdateDiscussionPayload,
  DiscussionListResponse,
  UnreadCountResponse,
  MarkAsReadPayload,
} from '@/types/discussion';

const API_BASE = env.apiTasklistBaseUrl;

/**
 * Tenant Discussion Service - API for discussions without checklist instance dependency
 * Endpoints: /:tenant_id/discussions
 */
export const TenantDiscussionService = {
  // Create Discussion
  useCreateDiscussion: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async ({ tenantId, payload }: { tenantId: number; payload: CreateDiscussionPayload }) => {
        const { data } = await axiosInstance.post<{
          success: boolean;
          message: string;
          data: ChecklistDiscussion;
        }>(`${API_BASE}/api/v1/compro/${tenantId}/discussions`, payload);

        return data.data;
      },
      onSuccess: (_, variables) => {
        // Invalidate discussions list
        queryClient.invalidateQueries({
          queryKey: ['tenant-discussions', variables.tenantId],
        });
        // Invalidate unread count
        queryClient.invalidateQueries({
          queryKey: ['tenant-discussions-unread', variables.tenantId],
        });
      },
    });
  },

  // Get Discussions (with pagination)
  useGetDiscussions: (tenantId?: number, page = 1, pageSize = 20) => {
    return useQuery<DiscussionListResponse>({
      queryKey: ['tenant-discussions', tenantId, page, pageSize],
      queryFn: async () => {
        if (!tenantId) throw new Error('Missing tenantId');

        const { data } = await axiosInstance.get<{
          success: boolean;
          message: string;
          data: DiscussionListResponse;
        }>(`${API_BASE}/api/v1/compro/${tenantId}/discussions`, {
          params: { page, page_size: pageSize },
        });

        return data.data;
      },
      enabled: !!tenantId,
    });
  },

  // Update Discussion
  useUpdateDiscussion: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async ({
        tenantId,
        discussionId,
        payload,
      }: {
        tenantId: number;
        discussionId: number;
        payload: UpdateDiscussionPayload;
      }) => {
        const { data } = await axiosInstance.put<{
          success: boolean;
          message: string;
          data: null;
        }>(`${API_BASE}/api/v1/compro/${tenantId}/discussions/${discussionId}`, payload);

        return data;
      },
      onSuccess: (_, variables) => {
        // Invalidate discussions list
        queryClient.invalidateQueries({
          queryKey: ['tenant-discussions', variables.tenantId],
        });
      },
    });
  },

  // Delete Discussion
  useDeleteDiscussion: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async ({ tenantId, discussionId }: { tenantId: number; discussionId: number }) => {
        const { data } = await axiosInstance.delete<{
          success: boolean;
          message: string;
          data: null;
        }>(`${API_BASE}/api/v1/compro/${tenantId}/discussions/${discussionId}`);

        return data;
      },
      onSuccess: (_, variables) => {
        // Invalidate discussions list
        queryClient.invalidateQueries({
          queryKey: ['tenant-discussions', variables.tenantId],
        });
      },
    });
  },

  // Get Unread Count
  useGetUnreadCount: (tenantId?: number) => {
    return useQuery<UnreadCountResponse>({
      queryKey: ['tenant-discussions-unread', tenantId],
      queryFn: async () => {
        if (!tenantId) throw new Error('Missing tenantId');

        const { data } = await axiosInstance.get<{
          success: boolean;
          message: string;
          data: UnreadCountResponse;
        }>(`${API_BASE}/api/v1/compro/${tenantId}/discussions/unread-count`);

        return data.data;
      },
      enabled: !!tenantId,
      refetchInterval: 30000, // Poll every 30 seconds
    });
  },

  // Mark as Read
  useMarkAsRead: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async ({ tenantId, payload }: { tenantId: number; payload: MarkAsReadPayload }) => {
        const { data } = await axiosInstance.post<{
          success: boolean;
          message: string;
          data: null;
        }>(`${API_BASE}/api/v1/compro/${tenantId}/discussions/mark-read`, payload);

        return data;
      },
      onSuccess: (_, variables) => {
        // Invalidate unread count
        queryClient.invalidateQueries({
          queryKey: ['tenant-discussions-unread', variables.tenantId],
        });
      },
    });
  },
};
